import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import FlagIcon from "@is/components/FlagIcon";
import {
  CountryCode,
  LocalStorageKeys,
  ROUTES,
  TranslationContext,
} from "@is/constants";
import {
  useConfiguration,
  useStoreCurrencies,
  useStoreEvents,
} from "@is/hooks";
import { useBasket, useManageBasket } from "@is/hooks/basket";
import { useLanguages } from "@is/hooks/languages/useLanguages";
import { LocalStorageService } from "@is/services";
import { TrackingEventTypes } from "@is/services/EventTracking";
import {
  convertAlpha3ToAlpha2,
  convertCountryCodeToCountryName,
  getLanguageFromLocalStorage,
} from "@is/utils";

type Props = {
  onClose: () => void;
};

const useManageLanguageAndCurrencyModal = ({ onClose }: Props) => {
  const { languages } = useLanguages();
  const { t } = useTranslation(TranslationContext.General);

  const { checkoutGeneral } = useConfiguration();

  const language = getLanguageFromLocalStorage();
  const country = LocalStorageService.getItem(LocalStorageKeys.CountryCode);

  const { error: basketItemsError } = useBasket({
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
  });

  const { handleUpdateBasket, basketItems } = useManageBasket();

  const [selectedLanguage, setSelectedLanguage] = useState(language ?? null);
  const [selectedCountry, setSelectedCountry] = useState(country ?? null);

  const { currencies } = useStoreCurrencies();

  const { storeEvent } = useStoreEvents();

  const availableLanguages = useMemo(
    () =>
      languages?.map((language) => ({
        value: language.code,
        label: `${language.name} (${language.code?.toUpperCase()})`,
      })) ?? [],
    [languages]
  );

  const availableDeliveryCountries = useMemo(
    () =>
      currencies?.map((currency) => ({
        value: currency.countryCode,
        label: `${convertCountryCodeToCountryName(
          currency.countryCode
        )} (${convertAlpha3ToAlpha2(currency.countryCode)})`,
        node: (
          <FlagIcon
            countryCode={
              convertAlpha3ToAlpha2(currency.countryCode) as CountryCode
            }
          />
        ),
      })) ?? [],
    [currencies]
  );

  const languageOrCurrencyChanged = useMemo(
    () => selectedCountry !== country || selectedLanguage !== language,
    [selectedCountry, country, selectedLanguage, language]
  );

  const enableCountrySelection = useMemo(
    () => checkoutGeneral?.enableCountrySelection,
    [checkoutGeneral]
  );

  const handleModalClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleApply = useCallback(async () => {
    if (
      !basketItemsError &&
      !!basketItems?.length &&
      selectedCountry !== country
    ) {
      await handleUpdateBasket({
        items: basketItems.map((item) => ({
          ...item,
          quantity: 0,
        })),
      });
    }

    onClose();

    if (selectedLanguage !== language) {
      LocalStorageService.setItem(
        LocalStorageKeys.LanguageCode,
        selectedLanguage
      );

      storeEvent({
        eventType: TrackingEventTypes.LanguageChanged,
        data: {
          selectedLanguage,
        },
        createdAt: new Date().toISOString(),
      });
    }

    if (selectedCountry !== country) {
      LocalStorageService.setItem(
        LocalStorageKeys.CountryCode,
        selectedCountry
      );

      storeEvent({
        eventType: TrackingEventTypes.CountryChanged,
        data: {
          selectedLanguage,
        },
        createdAt: new Date().toISOString(),
      });
    }

    storeEvent({
      eventType: TrackingEventTypes.LanguageAndCurrencyModalClose,
      data: {},
      createdAt: new Date().toISOString(),
    });

    window.location.href = "/instore" + ROUTES.EntryView;
  }, [
    basketItems,
    basketItemsError,
    country,
    handleUpdateBasket,
    language,
    onClose,
    selectedCountry,
    selectedLanguage,
    storeEvent,
  ]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSelectLanguage = useCallback((value: string[]) => {
    setSelectedLanguage(value[0]);
  }, []);

  const handleSelectCountry = useCallback((value: string[]) => {
    setSelectedCountry(value[0]);
  }, []);

  return {
    selectedLanguage,
    selectedCountry,
    handleModalClose,
    handleApply,
    handleCancel,
    handleSelectLanguage,
    handleSelectCountry,
    languageOrCurrencyChanged,
    availableLanguages,
    availableDeliveryCountries,
    enableCountrySelection,
    t,
  };
};

export default useManageLanguageAndCurrencyModal;
